// packages/core/src/learning/school-modules/inequality-awareness.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Inequality awareness". surface: 'school',
// homeSchool: 'inequality-awareness'. module id 'school-inequality-awareness'
// matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #54759e) — flag for the presentation-tuning pass.
// tier/pillar: structural/worldview lens (SchoolMeta bucket
// 'macro-wealth-perspectives') → tier 'growth', pillar 'money-psychology-core'
// (per the protocol default: worldview/mindset lenses → growth /
// money-psychology-core). Same mapping as the sibling anti-fragility and
// hard-money schools. Flag for the presentation-tuning pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_INEQUALITY_AWARENESS_MODULE: LearningModule = {
	id: 'school-inequality-awareness',
	title: 'Inequality awareness',
	description:
		'The lens that names the unequal terrain behind money outcomes — starting lines, luck, context, and uneven risk — so the plan stays honest, without letting structural truth collapse into defeat.',
	emoji: '⚖️', // PLACEHOLDER — tune in presentation pass
	accentColor: '#54759e', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'inequality-awareness',
	lessons: [
		{
			id: 'school-inequality-awareness-01',
			title: 'Starting lines are not equal',
			description:
				'Learn why Inequality awareness sees starting conditions as part of every money outcome.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['inequality-awareness'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'unequal-starting-lines',
				'hidden-support',
				'financial-terrain',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'unequal starting lines',
				'hidden support',
				'financial terrain',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money advice often talks like everyone begins from the same place. Inequality awareness says that is not reality.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes your starting line affects your money path. Family support, debt, housing costs, health, education, immigration pressure, neighborhood, network, and timing all change what choices are realistic.',
				},
				{
					type: 'bullet_list',
					items: [
						'Support changes risk',
						'Housing changes cash flow',
						'Family obligations change speed',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging outcomes without seeing the terrain.',
				},
				{
					type: 'example',
					text: 'Two people earn the same income. One lives at home while saving, has parents who can help in an emergency, and has no family obligations. The other pays rent, sends money to family, and has no backup. Same income, very different money reality.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can become heavy if it stops at unfairness. Naming the terrain should make the plan more honest, not make action feel pointless.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens begins with Dashboard. Look at the real picture, including support, obligations, and risk. The goal is not shame. The goal is accuracy.',
				},
				{
					type: 'key_takeaway',
					text: 'A fair money plan starts by admitting that starting lines are not the same.',
				},
			],
			audioScript:
				'Inequality awareness starts with something simple and uncomfortable: people do not start from the same place. That sounds obvious, but a lot of financial advice acts like it is not true. It talks as if every adult is standing on the same starting line with the same choices, the same backup, the same cost of living, the same family help, and the same room to make mistakes.\n\nThat is not real life. Starting conditions matter. One person might graduate with help, live at home, borrow a car, stay on a family phone plan, and get emotional support while they figure things out. Another person might graduate with debt, pay full rent, help parents, pay for siblings, commute farther, and have no one to call if the car breaks. Those two people might both earn $45,000, but their money lives are not the same.\n\nThis lens does not say personal choices do not matter. They do matter. But choices happen inside conditions. If rent takes half your income, a budgeting lesson lands differently. If you are supporting family, savings advice lands differently. If you have health costs, unstable work, unsafe housing, or no emergency backup, every decision carries more weight. Risk is not evenly distributed.\n\nA simple example makes it clear. Two people both save $500 per month. One has parents who would cover an emergency. The other is the emergency plan for their family. On paper, the savings rate looks the same. In real life, one person can take more risks, move jobs, try a new city, or recover from a setback more easily. The other person has to protect the base because more people depend on it. Same habit, different pressure.\n\nInequality awareness protects against lazy judgment. It pushes back on the idea that everyone behind is irresponsible and everyone ahead is smarter. Sometimes people are disciplined and still under pressure. Sometimes people are careless and still cushioned by support. Sometimes luck, timing, housing, family, geography, and health explain more than motivational slogans do.\n\nThe danger is stopping there. If this lens only says “the system is unfair,” it can make people feel stuck. That is not enough. The useful version says, “Name the unfair terrain so the plan can be honest.” If you know the real obstacles, you can build around them better. You can stop blaming yourself for things outside your control while still taking ownership of the next real move.\n\nInside Nisk, start with your Dashboard. What support do you have? What obligations do you carry? What expenses are not optional? What risks would hit you harder than someone with a larger safety net? This is not about self pity. It is about seeing the real board before you make a move.\n\nA fair plan starts with the truth. Starting lines are not equal. Your strategy should know that.',
			flashcards: [
				{
					id: 'school-inequality-awareness-01-fc-1',
					role: 'main_idea',
					front: 'What does starting line mean?',
					back: 'The support, risk, cost, and obligations a person begins with.',
				},
				{
					id: 'school-inequality-awareness-01-fc-2',
					role: 'watch_out',
					front: 'What is the judgment trap?',
					back: 'Comparing outcomes without seeing different starting conditions.',
				},
				{
					id: 'school-inequality-awareness-01-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Add support, obligations, and risk to your money picture.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your real picture',
			},
			revision: 1,
		},
		{
			id: 'school-inequality-awareness-02',
			title: 'Advice changes with context',
			description:
				'Learn why Inequality awareness challenges one size fits all money rules.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['inequality-awareness'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'context-aware-advice',
				'money-constraints',
				'realistic-planning',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'context aware advice',
				'money constraints',
				'realistic planning',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A rule can sound smart and still miss the person in front of it. Money advice changes when rent, income, debt, family, health, and safety change.',
				},
				{
					type: 'paragraph',
					text: 'Inequality awareness believes context matters. The same advice can be useful for one person and unrealistic for another because their constraints are different.',
				},
				{
					type: 'bullet_list',
					items: [
						'A high earner has more slack',
						'A caregiver has less time',
						'A renter in a costly city faces different pressure',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating every money rule like it applies equally to every life.',
				},
				{
					type: 'example',
					text: '“Just move somewhere cheaper” sounds simple until someone has custody needs, medical care nearby, elder care duties, a job network, or no cash to move. The advice may work for some people. It is not universal.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is rejecting every general principle because context matters. Context should adjust the plan, not destroy the need for a plan.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens turns rules into questions. Before accepting any money rule, ask what assumptions it makes about your income, location, family, safety, and time.',
				},
				{
					type: 'key_takeaway',
					text: 'Good money advice needs the person’s real life attached to it.',
				},
			],
			audioScript:
				'Inequality awareness changes how you hear money advice. It does not automatically reject advice. It asks what assumptions the advice is making. That is important because a rule can be useful in one life and unrealistic in another.\n\nTake a common line: “Just move somewhere cheaper.” Sometimes that is good advice. Moving can lower costs and create breathing room. But sometimes it ignores reality. A person may need to stay near a job, family care, medical support, childcare, transportation, community, or immigration support. They may not have the cash for deposits, movers, a new car, or missed work. They may live somewhere expensive because the income opportunities are there. A cheaper place can create different costs.\n\nAnother example is “save twenty percent.” Great if it is possible. But if someone is earning low wages in a high cost area, helping family, paying debt, and managing health expenses, that number may create shame instead of clarity. The better question is not “Why are you failing the rule?” The better question is “What is the next realistic amount that still builds momentum?”\n\nThis lens is useful because it keeps advice grounded. It sees constraints. Rent is a constraint. Wages are a constraint. Family obligations are a constraint. Time is a constraint. Mental health can be a constraint. Safety can be a constraint. Education access, transportation, and network all matter too. A plan that ignores constraints is not ambitious. It is pretend.\n\nBut there is a blind spot. Context can become an excuse to reject every principle. That is not useful either. The fact that a rule does not fit perfectly does not mean no rule can help. It means the rule needs translation. Maybe the savings target is smaller. Maybe the timeline is longer. Maybe the first goal is runway, not investing. Maybe the money move is income, not cutting. Maybe the best action is a hard conversation with family. Maybe the plan starts with stability before speed.\n\nThe mature version of Inequality awareness holds two truths at once. One: structural barriers are real and can make money advice harder to apply. Two: the next practical move still matters. That balance is the whole point. If you ignore barriers, you shame people. If you ignore agency, you trap people.\n\nInside Nisk, use this lens whenever a rule sounds too clean. Ask what the rule assumes. Does it assume stable income? Does it assume no caregiving? Does it assume a low cost city? Does it assume a car? Does it assume family help? Does it assume free time? Once you see the assumptions, you can adjust the plan.\n\nGood advice is not just correct in theory. It has to survive contact with a real life.',
			flashcards: [
				{
					id: 'school-inequality-awareness-02-fc-1',
					role: 'main_idea',
					front: 'Why does context matter?',
					back: 'The same rule can fit one life and fail another.',
				},
				{
					id: 'school-inequality-awareness-02-fc-2',
					role: 'watch_out',
					front: 'What is the context trap?',
					back: 'Rejecting every principle because no rule fits perfectly.',
				},
				{
					id: 'school-inequality-awareness-02-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'What assumptions does this advice make about my life?',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Test the advice',
				prompt: 'Write one money rule you hear often, then list the assumptions it makes about income, family, location, safety, and time.',
			},
			revision: 1,
		},
		{
			id: 'school-inequality-awareness-03',
			title: 'Luck is not virtue',
			description:
				'Learn why Inequality awareness separates good outcomes from moral superiority.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['inequality-awareness'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['luck-awareness', 'outcome-humility', 'moral-judgment'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['luck awareness', 'outcome humility', 'moral judgment'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money outcomes can make people overconfident. If they win, they may assume it was all skill. If someone else struggles, they may assume it was all bad choices.',
				},
				{
					type: 'paragraph',
					text: 'Inequality awareness believes luck and timing matter. Birth family, health, school quality, housing market, job market, social network, and economic cycles can shape outcomes before effort even shows up.',
				},
				{
					type: 'bullet_list',
					items: [
						'Luck can help quietly',
						'Bad timing can punish good choices',
						'Outcomes do not prove character',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning financial outcomes into moral rankings.',
				},
				{
					type: 'example',
					text: 'Someone bought a home before prices rose and rates changed. Another person reached adulthood after housing became much more expensive. The first person may have made a good decision, but timing also helped.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that luck awareness can sound like it dismisses effort. It should not. Effort matters. Skill matters. Discipline matters. The point is that they are not the only forces in the room.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should make you more honest and less judgmental. If you are ahead, stay humble. If you are behind, stop turning every obstacle into personal shame.',
				},
				{
					type: 'key_takeaway',
					text: 'Outcomes matter, but they do not tell the whole story of effort, luck, and timing.',
				},
			],
			audioScript:
				'Inequality awareness has a strong warning: do not turn money outcomes into moral rankings. That is easy to do. People who are ahead may believe they are ahead only because they worked harder, chose better, or had more discipline. People who are behind may believe they are behind because they are broken, lazy, or too late. Both stories can be incomplete.\n\nLuck matters. Timing matters. Family matters. Health matters. Location matters. Housing markets matter. Job markets matter. Who teaches you money matters. Who can help you during a crisis matters. Whether you had room to make mistakes matters. Whether you had to support others early matters. None of these fully replace effort, but they shape what effort can do.\n\nThink about housing. Someone who bought a home before a major price increase may have built equity quickly. That person may have made a responsible choice. But timing helped. Another person may be just as disciplined, but reaches home buying age after prices and rates have moved against them. Their struggle does not automatically mean they are less responsible. The terrain changed.\n\nThe same applies to careers. Graduating into a strong job market is different from graduating into a recession. Having a parent who knows how resumes, interviews, and negotiation work is different from figuring it all out alone. Having a stable place to live while applying to jobs is different from trying to survive while searching. Again, effort still matters. But effort does not operate in a vacuum.\n\nThe useful part of this lens is humility. If you are ahead, it asks you not to confuse support, timing, and luck with pure virtue. That does not erase your work. It makes your story more accurate. If you are behind, it asks you not to turn every obstacle into shame. That does not remove responsibility. It gives you a cleaner view of what you are actually fighting.\n\nThe blind spot is using luck to dismiss effort entirely. That is not the goal. Some people really do make better choices. Some people really do practice, save, learn, take risks, and recover. Agency is real. The better frame is: outcomes come from effort, choices, systems, luck, timing, and support all interacting. When you see that, you can be both responsible and fair.\n\nInside Nisk, this should change the emotional tone of learning. You are not here to worship winners or shame people who struggled. You are here to understand the forces and make better decisions from where you are. Your Dashboard shows your current reality. It does not judge your character. Simulate helps you test moves. It does not pretend everyone has the same options.\n\nLuck is not virtue. Struggle is not proof of failure. The work is to see clearly and still move.',
			flashcards: [
				{
					id: 'school-inequality-awareness-03-fc-1',
					role: 'main_idea',
					front: 'Why does luck matter?',
					back: 'Timing, family, health, markets, and support shape money outcomes.',
				},
				{
					id: 'school-inequality-awareness-03-fc-2',
					role: 'watch_out',
					front: 'What is the moral trap?',
					back: 'Treating wealth as virtue and struggle as character failure.',
				},
				{
					id: 'school-inequality-awareness-03-fc-3',
					role: 'use_it',
					front: 'How should you respond?',
					back: 'Stay humble if ahead and stop shaming yourself if behind.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Separate luck and effort',
				prompt: 'Write one money outcome in your life, then list what came from effort, what came from timing, and what came from support or barriers.',
			},
			revision: 1,
		},
		{
			id: 'school-inequality-awareness-04',
			title: 'Barriers change risk',
			description:
				'Learn why Inequality awareness sees the same decision as more dangerous for people with less backup.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['inequality-awareness'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['unequal-risk', 'safety-net-gap', 'downside-exposure'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['unequal risk', 'safety net gap', 'downside exposure'],
			blocks: [
				{
					type: 'paragraph',
					text: 'The same choice is not the same risk for everyone. A mistake that is annoying for one person can be life changing for another.',
				},
				{
					type: 'paragraph',
					text: 'Inequality awareness believes risk depends on backup. Savings, family help, stable housing, health, legal status, transportation, and job security all change how hard a setback hits.',
				},
				{
					type: 'bullet_list',
					items: [
						'Less backup means less room for error',
						'More obligations make risk heavier',
						'A small shock can start a chain reaction',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging risk by the decision alone instead of the safety net behind it.',
				},
				{
					type: 'example',
					text: 'Two people try a career change. One can live with family if it fails. The other pays full rent and supports relatives. The same move carries very different downside.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that avoiding every risk can keep people stuck. The answer is not never take risks. The answer is to size risks around the backup you actually have.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens points to runway. Before making a move, ask what failure would cost and what support would remain if things go wrong.',
				},
				{
					type: 'key_takeaway',
					text: 'Risk is not only the move. Risk is the move plus the safety net behind it.',
				},
			],
			audioScript:
				'Inequality awareness changes how you think about risk. Most people judge a decision by the decision itself. Is it smart to change jobs? Is it smart to move? Is it smart to start a side business? Is it smart to go back to school? Is it smart to buy a car? But this lens says the same decision can be a very different risk depending on the safety net behind it.\n\nA person with savings, family support, stable housing, good health, a strong network, and no dependents can recover from mistakes more easily. That does not mean their choices are easy, but they have more room. A person with no savings, no backup, health costs, family obligations, unstable housing, or a fragile job has less room for error. The same setback can hit much harder.\n\nImagine two people considering a career change. One can move back home if the transition fails. Their parents can help with food, rent, or a car repair. They have health coverage and no one depends on their income. The other person pays full rent, sends money to family, and has no place to land if the job search takes longer than expected. The career change may be reasonable for both, but the downside is not equal.\n\nThis is why advice like “take the risk” can be dangerous when it ignores backup. Risk is not just courage. Risk is exposure. If failure means temporary embarrassment for one person and eviction for another, those are not the same decision. Inequality awareness does not say the second person should never take a risk. It says the risk has to be sized differently.\n\nThis lens also explains why some people seem more conservative with money. It may not be fear. It may be experience. If you have seen one bill create a chain reaction, you learn to protect the base. If you have watched family instability up close, you may need more runway before making a move. That is not weakness. That is information.\n\nThe blind spot is becoming so aware of downside that you never move. Avoiding every risk can keep people stuck in low pay, bad housing, unhealthy work, or small opportunities. The goal is not zero risk. The goal is survivable risk. Smaller tests. More runway. Clearer limits. Backup plans. Skills that travel. Moves that can fail without destroying the base.\n\nInside Nisk, runway is the key question. How long can you last if the move takes longer? What happens if income dips? What support do you really have? What obligations keep running no matter what? What would make the move safer? Simulate can help you test the numbers, but the relationship and family context matter too.\n\nRisk is not only the move. Risk is the move plus the safety net behind it. See both before you judge yourself or anyone else.',
			flashcards: [
				{
					id: 'school-inequality-awareness-04-fc-1',
					role: 'main_idea',
					front: 'Why is risk unequal?',
					back: 'Backup, obligations, and safety nets change how hard failure hits.',
				},
				{
					id: 'school-inequality-awareness-04-fc-2',
					role: 'watch_out',
					front: 'What advice can be dangerous?',
					back: 'Telling people to take risks without knowing their downside.',
				},
				{
					id: 'school-inequality-awareness-04-fc-3',
					role: 'use_it',
					front: 'What should you check first?',
					back: 'Runway, obligations, support, and what failure would cost.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your backup',
			},
			revision: 1,
		},
		{
			id: 'school-inequality-awareness-05',
			title: 'Awareness is not surrender',
			description:
				'Learn where Inequality awareness breaks down when structural truth turns into personal defeat.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['inequality-awareness'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'agency-with-barriers',
				'practical-next-step',
				'clear-eyed-action',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'agency with barriers',
				'practical next step',
				'clear eyed action',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Seeing unfairness clearly can be freeing. It can also become heavy if it makes every action feel useless.',
				},
				{
					type: 'paragraph',
					text: 'Inequality awareness believes structural forces are real, but awareness should sharpen agency, not erase it. The goal is not to pretend barriers do not exist. The goal is to choose the next move with fewer lies.',
				},
				{
					type: 'bullet_list',
					items: [
						'Name the barrier',
						'Stop the shame',
						'Find the next real option',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning a true obstacle into a complete identity.',
				},
				{
					type: 'example',
					text: 'Someone realizes housing costs and family obligations slow their progress. That truth matters. But they may still be able to build a smaller runway, improve income, renegotiate support, reduce one fixed cost, or choose a better sequence.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is emotional. Agency can sound insulting when someone is exhausted. Structural awareness can sound hopeless when no next step is named. The stronger version needs both compassion and movement.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens ends with a practical question: given the terrain, what move is still available? It may be small. Small still counts.',
				},
				{
					type: 'key_takeaway',
					text: 'Inequality awareness should remove false shame, not remove your next move.',
				},
			],
			audioScript:
				'Inequality awareness is meant to tell the truth. Some people start with more help. Some start with more risk. Housing, wages, education, health, family support, discrimination, debt, geography, and timing all shape money outcomes. Naming those forces can be freeing because it removes false shame. You stop telling yourself that every hard thing is proof you failed.\n\nBut this lens has a danger. If it only tells you the terrain is unfair, it can make every action feel pointless. That is not the version Nisk should teach. Awareness is not surrender. The point is not to say, “The system is hard, so nothing matters.” The point is to say, “The system is hard, so the plan needs to be honest.”\n\nThere is a difference between blame and diagnosis. Blame says, “This is all your fault,” or “None of this is your fault.” Both can become too simple. Diagnosis says, “Here are the forces affecting the situation. Here is what is outside your control. Here is what is inside your control. Here is the next move that still exists.” That is more useful.\n\nImagine someone realizes they are not saving as fast as a friend. The friend lives at home, has no family obligations, and works near better opportunities. This person pays rent, helps family, and has higher transportation costs. Inequality awareness helps them stop making an unfair comparison. But after that, the next question still matters. Can they build a smaller emergency fund first? Can they have a clearer family support boundary? Can they raise income over time? Can they reduce one fixed cost? Can they choose a sequence that fits their real life instead of copying someone else?\n\nThe next move may be small. That is okay. Small is not fake. Small can be honest. A person under pressure may not be able to save $1,000 this month, but maybe they can protect $50. They may not be able to move now, but they can learn the costs. They may not be able to stop supporting family, but they can name a limit. They may not be able to change careers immediately, but they can build one proof piece.\n\nThe emotional balance matters. Telling an exhausted person “just take responsibility” can sound cruel. Telling them “nothing is in your control” can be just as damaging. The stronger message is: the barriers are real, and you still deserve a plan.\n\nInside Nisk, this is the heart of the lens. Dashboard shows the truth without moral judgment. Simulate lets you test possible moves. Reflection helps you separate what is unfair from what is actionable. You do not need a perfect path. You need the next real option.\n\nInequality awareness should remove false shame. It should not remove your agency.',
			flashcards: [
				{
					id: 'school-inequality-awareness-05-fc-1',
					role: 'main_idea',
					front: 'What should awareness do?',
					back: 'Remove false shame and make the next move more honest.',
				},
				{
					id: 'school-inequality-awareness-05-fc-2',
					role: 'watch_out',
					front: 'Where does this lens break?',
					back: 'When structural truth turns into defeatism.',
				},
				{
					id: 'school-inequality-awareness-05-fc-3',
					role: 'use_it',
					front: 'What question helps?',
					back: 'Given the terrain, what move is still available?',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Find the next real option',
				prompt: 'Write one barrier that is real, then write one next move that is still possible without pretending the barrier is fake.',
			},
			revision: 1,
		},
	],
};
